import { type FunctionComponent } from 'react'
import styled from '@emotion/styled'
import { ItemsList, TDSStylesProvider, Text } from '@retailer-platform/shared-components/src/tds'
import { LoadingPage, ErrorPage } from '@retailer-platform/dashboard/gin-and-tonic'
import { usePartnerContext } from '@retailer-platform/dashboard/utils'
import { useDomainMessages } from '../../../utils/domain/intl'
import {
  usePartnerNotificationChannels,
  usePlatformNotificationChannels,
} from './hooks/useNotificationChannelsQuery'
import { PlatformChannelsSection } from './components/PlatformChannelsSection'
import { BannerChannelsSection } from './components/BannerChannelsSection'
import { PartnerChannelsSection } from './components/PartnerChannelsSection'

const ContentWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
})

const StyledHeader = styled.div({
  padding: '24px 80px 12px',
})

const PageDescription = styled.div(({ theme }) => ({
  marginTop: '8px',
  color: theme.colors.systemGrayscale80,
}))

export const NotificationSettings: FunctionComponent = () => {
  const partnerContext = usePartnerContext()

  const {
    data: platformChannelsData,
    loading: platformLoading,
    error: platformError,
  } = usePlatformNotificationChannels()

  const {
    data: partnerChannelsData,
    loading: partnerLoading,
    error: partnerError,
  } = usePartnerNotificationChannels(partnerContext?.partner?.id)

  const i18n = useDomainMessages({
    title: 'notificationsDomain.settings.pageTitle',
    description: 'notificationsDomain.settings.pageDescription',
  })

  if (platformLoading || partnerLoading) return <LoadingPage />
  if (platformError || partnerError) {
    return (
      <TDSStylesProvider>
        <ErrorPage />
      </TDSStylesProvider>
    )
  }

  return (
    <TDSStylesProvider>
      <div data-testid="notification-settings-page">
        <ItemsList>
          <ItemsList.Header>
            <StyledHeader>
              <Text typography="headline">{i18n.title}</Text>
              <PageDescription>
                <Text typography="bodyRegular">{i18n.description}</Text>
              </PageDescription>
            </StyledHeader>
          </ItemsList.Header>
          <ItemsList.Main>
            <ContentWrapper>
              {platformChannelsData?.notificationChannelsForCurrentAccount &&
                platformChannelsData.notificationChannelsForCurrentAccount.length > 0 && (
                  <PlatformChannelsSection
                    platformChannels={platformChannelsData.notificationChannelsForCurrentAccount}
                  />
                )}
              {partnerChannelsData?.notificationChannelsForCurrentAccount &&
                partnerChannelsData.notificationChannelsForCurrentAccount.length > 0 && (
                  <PartnerChannelsSection
                    partnerChannels={partnerChannelsData.notificationChannelsForCurrentAccount}
                  />
                )}
              <BannerChannelsSection />
            </ContentWrapper>
          </ItemsList.Main>
        </ItemsList>
      </div>
    </TDSStylesProvider>
  )
}
